import React, { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { FiExternalLink } from "react-icons/fi";
import { PageRenderer, RendererRuntimeProvider, type All8PageContent } from "@apa/core-renderers";
import { LoadingSpinner } from "../LoadingSpinner";
import PageConnectionsButton from "../Connections/PageConnectionsButton";

export const PREVIEW_MAX_WIDTH = 1280;
export const PREVIEW_VIEWPORTS = {
	mobile: 430,
	tablet: 960,
	desktop: 1280,
	full: null as number | null
};
export type PreviewViewportKey = keyof typeof PREVIEW_VIEWPORTS;

const PreviewPane: React.FC<{
	components: All8PageContent;
	width: number | null;
	containerWidth: number | null;
	runtimeConfig: any;
}> = ({ components, width, containerWidth, runtimeConfig }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;
		const doc = iframe.contentDocument;
		if (!doc) return;

		doc.open();
		doc.write("<!doctype html><html><head></head><body><div id='preview-root'></div></body></html>");
		doc.close();

		const head = doc.head;
		document.querySelectorAll("link[rel='stylesheet'], style").forEach((node) => {
			head.appendChild(node.cloneNode(true));
		});

		const mountNode = doc.getElementById("preview-root");
		if (!mountNode) return;

		const root = createRoot(mountNode);
		rootRef.current = root;
		root.render(
			<RendererRuntimeProvider value={runtimeConfig}>
				<div className="page-editor-preview-inner">
					<PageRenderer components={components} />
				</div>
			</RendererRuntimeProvider>
		);

		return () => {
			root.unmount();
		};
	}, [components, runtimeConfig]);

	// Lock iframe layout to the chosen viewport width (mobile/tablet/desktop) so content renders at real size
	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe?.contentDocument?.head) return;
		let meta = iframe.contentDocument.querySelector('meta[name="viewport"]');
		if (!meta) {
			meta = iframe.contentDocument.createElement("meta");
			meta.setAttribute("name", "viewport");
			iframe.contentDocument.head.appendChild(meta);
		}
		meta.setAttribute("content", width != null ? `width=${width}` : "width=device-width");
	}, [width]);

	const frameWidth = width ? Math.min(width, PREVIEW_MAX_WIDTH) : undefined;
	// Use a 2px buffer when scaling down so the full content fits (avoids right-edge clipping, esp. tablet)
	const fitBuffer = 2;
	// Cap scale for tablet/desktop (960px+) so the preview doesn’t feel overzoomed; keep full content visible with margin
	const maxScaleTabletDesktop = 0.9;
	// When container width is unknown, use full-width mode so we never overflow (stable on small window + reload)
	const hasValidContainer = containerWidth != null && containerWidth > 0;
	const effectiveContainerWidth = hasValidContainer ? containerWidth : null;
	const fixedViewport = frameWidth != null && effectiveContainerWidth != null;
	const rawScale =
		fixedViewport
			? Math.min(1, Math.max(0.3, (effectiveContainerWidth - fitBuffer) / frameWidth))
			: 1;
	const scale =
		frameWidth != null && frameWidth >= PREVIEW_VIEWPORTS.tablet
			? Math.min(rawScale, maxScaleTabletDesktop)
			: rawScale;

	// Fixed viewport (mobile/tablet/desktop): render at real size (frameWidth), scale to fit container.
	// Wrapper uses displayed size (frameWidth * scale) so layout fits and doesn’t overflow.
	const rawDisplayWidth = fixedViewport ? frameWidth * scale : null;
	const displayWidthPx =
		rawDisplayWidth != null && effectiveContainerWidth != null
			? Math.min(rawDisplayWidth, effectiveContainerWidth)
			: rawDisplayWidth;
	const innerHeight = fixedViewport && scale < 1 ? `${100 / scale}%` : "100%";
	// Center the preview when the panel is wider than the scaled frame (e.g. mobile on a big panel)
	const centerInPanel =
		fixedViewport && displayWidthPx != null && effectiveContainerWidth != null && displayWidthPx < effectiveContainerWidth;

	return (
		<div
			className="page-editor-preview h-full"
			style={{
				width: displayWidthPx != null ? `${displayWidthPx}px` : "100%",
				maxWidth: "100%",
				height: "100%",
				marginLeft: centerInPanel ? "auto" : 0,
				marginRight: "auto",
				overflow: "hidden",
				minWidth: 0
			}}
		>
			<div
				style={{
					width: fixedViewport ? `${frameWidth}px` : "100%",
					height: innerHeight,
					transform: fixedViewport ? `scale(${scale})` : "none",
					transformOrigin: "top left"
				}}
			>
				<iframe
					ref={iframeRef}
					title="Page preview"
					style={{
						width: "100%",
						border: "none",
						height: "100%",
						background: "white",
						overflow: "auto"
					}}
				/>
			</div>
		</div>
	);
};

type PageEditorPreviewPanelProps = {
	previewViewport: PreviewViewportKey;
	onViewportChange: (viewport: PreviewViewportKey) => void;
	/** URL to open in new tab for preview site; empty hides the button */
	previewPageUrl: string;
	/** URL to open in new tab for production site; empty hides the button */
	productionPageUrl: string;
	previewContainerRef: React.RefObject<HTMLDivElement>;
	previewCss: string;
	previewComponents: All8PageContent;
	previewWidth: number | null;
	previewContainerWidth: number | null;
	runtimeConfig: any;
	dynamicPreviewLabel?: string | null;
	onOpenDynamicPreview?: () => void;
	onClearDynamicPreview?: () => void;
	/** Optional min height for the preview container (px or CSS value). */
	minHeight?: number | string;
	/** Optional fixed height for the preview container (px or CSS value). */
	contentHeight?: number | string;
	/** When set, shows a "Connexions" button that opens this page's connections graph. */
	pageId?: string | null;
	pageTitle?: string | null;
};

/** Live page preview panel with responsive viewport simulation in an iframe. */
const PageEditorPreviewPanel: React.FC<PageEditorPreviewPanelProps> = ({
	previewViewport,
	onViewportChange,
	previewPageUrl,
	productionPageUrl,
	previewContainerRef,
	previewCss,
	previewComponents,
	previewWidth,
	previewContainerWidth,
	runtimeConfig,
	dynamicPreviewLabel,
	onOpenDynamicPreview,
	onClearDynamicPreview,
	minHeight,
	contentHeight,
	pageId,
	pageTitle
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4 flex flex-col h-full min-h-0">
		<div className="flex flex-wrap items-center justify-between gap-3">
			<div>
				<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Live preview</p>
				<p className="text-sm text-slate-600">Links are disabled here. Choose a viewport to simulate responsive layouts.</p>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				{onOpenDynamicPreview && (
					<button
						type="button"
						onClick={onOpenDynamicPreview}
						className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50"
					>
						Preview with content item
					</button>
				)}
				{dynamicPreviewLabel && (
					<span className="text-xs text-slate-500">
						Previewing: <span className="font-semibold text-slate-700">{dynamicPreviewLabel}</span>
					</span>
				)}
				{dynamicPreviewLabel && onClearDynamicPreview && (
					<button
						type="button"
						onClick={onClearDynamicPreview}
						className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:border-slate-300 hover:bg-slate-50"
					>
						Clear preview
					</button>
				)}
				{(Object.keys(PREVIEW_VIEWPORTS) as PreviewViewportKey[]).map((key) => {
					const isActive = previewViewport === key;
					const label =
						key === "mobile"
							? "Mobile"
							: key === "tablet"
							? "Tablet"
							: key === "desktop"
							? "Desktop"
							: "Full";
					return (
						<button
							key={key}
							type="button"
							onClick={() => onViewportChange(key)}
							className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
								isActive
									? "border border-slate-800 bg-slate-800 text-white shadow-sm"
									: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
							}`}
						>
							{label}
						</button>
					);
				})}
				{pageId ? <PageConnectionsButton pageId={pageId} pageTitle={pageTitle ?? undefined} /> : null}
				{previewPageUrl ? (
					<a
						href={previewPageUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiExternalLink className="shrink-0" aria-hidden />
						Preview
					</a>
				) : null}
				{productionPageUrl ? (
					<a
						href={productionPageUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiExternalLink className="shrink-0" aria-hidden />
						Production
					</a>
				) : null}
			</div>
		</div>
		<div
			className="bg-slate-50 px-2 py-4 flex-1 min-h-[360px] min-w-0 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 flex justify-center items-start"
			ref={previewContainerRef}
			style={{
				...(minHeight != null
					? { minHeight: typeof minHeight === "number" ? `${minHeight}px` : minHeight }
					: {}),
				...(contentHeight != null
					? {
							height: typeof contentHeight === "number" ? `${contentHeight}px` : contentHeight,
							minHeight: typeof contentHeight === "number" ? `${contentHeight}px` : contentHeight
					  }
					: {})
			}}
		>
			<style>{previewCss}</style>
			{previewComponents.length === 0 ? (
				<div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
					Add components to see a live preview.
				</div>
			) : (
				<PreviewPane
					components={previewComponents}
					width={previewWidth}
					containerWidth={previewContainerWidth}
					runtimeConfig={runtimeConfig}
				/>
			)}
		</div>
		<p className="text-xs text-slate-500">Preview updates instantly as you edit.</p>
		{previewComponents.length === 0 && (
			<div className="sr-only">
				<LoadingSpinner size="sm" inline />
			</div>
		)}
	</div>
);

export default PageEditorPreviewPanel;
